import { pantryTypography } from '@pantry/ui'
import type React from 'react'

// Prototype design tokens (breadstack-campaign-builder-final.html :root,
// L8–25) for the builder shell and steps. App chrome elsewhere keeps using
// pantryColors; the builder follows the prototype.

export const T = {
  green: '#1E7D4A',
  greenH: '#175f38',
  greenLight: '#E8F5EE',
  greenMid: '#d0ead9',
  amber: '#B45309',
  amberBg: '#FEF3C7',
  blue: '#1D4ED8',
  blueH: '#1740b0',
  blueBg: '#EFF6FF',
  red: '#DC2626',
  redBg: '#FEF2F2',
  purple: '#7C3AED',
  purpleBg: '#F5F3FF',
  teal: '#0F766E',
  tealBg: '#F0FDFA',
  orange: '#EA580C',
  orangeBg: '#FFF7ED',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  white: '#FFFFFF',
} as const

export const R = { sm: 6, md: 10, lg: 16 } as const

export const SHADOW = {
  sm: '0 1px 3px rgba(0,0,0,.08)',
  md: '0 4px 12px rgba(0,0,0,.10)',
  lg: '0 8px 28px rgba(0,0,0,.16)',
} as const

export const FONT = pantryTypography.fontFamily

// Type-card tints (prototype `.type-card.{email,sms,cart}`).
export const TYPE_TINT = {
  email: { fg: T.blue, bg: T.blueBg },
  sms: { fg: T.teal, bg: T.tealBg },
  abandoned_cart: { fg: T.orange, bg: T.orangeBg },
} as const

// Sidebar width (prototype `.builder-sidebar`) and the mini preview's
// inner width.
export const SIDEBAR_W = 360
export const PREVIEW_INNER_W = 312

// ── Shared style objects ────────────────────────────────────────────────────

export const sectionLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: T.gray400,
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
  marginBottom: 12,
}

export const formLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 12.5,
  fontWeight: 600,
  color: T.gray700,
  marginBottom: 6,
}

export const formHint: React.CSSProperties = {
  fontSize: 11.5,
  color: T.gray400,
  lineHeight: 1.5,
  marginTop: 6,
}

export const formError: React.CSSProperties = {
  fontSize: 12,
  color: T.red,
  marginTop: 4,
}

export const formInput: React.CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  padding: '9px 12px',
  border: `1.5px solid ${T.gray200}`,
  borderRadius: R.sm,
  fontSize: 13.5,
  color: T.gray800,
  outline: 'none',
  background: T.white,
  fontFamily: FONT,
}

export const formSelect: React.CSSProperties = {
  ...formInput,
  padding: '9px 32px 9px 12px',
  appearance: 'none',
  cursor: 'pointer',
  background: `${T.white} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") no-repeat right 10px center`,
}

const btnBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 10px',
  borderRadius: R.sm,
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
  border: 'none',
  fontFamily: FONT,
  whiteSpace: 'nowrap',
}

export const btn = {
  ghost: {
    ...btnBase,
    background: 'transparent',
    color: T.gray600,
    border: `1px solid ${T.gray300}`,
  } as React.CSSProperties,
  blue: {
    ...btnBase,
    background: T.blue,
    color: T.white,
  } as React.CSSProperties,
  primary: {
    ...btnBase,
    background: T.green,
    color: T.white,
  } as React.CSSProperties,
  link: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    color: T.green,
    fontFamily: FONT,
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
}

export const disabledStyle = (disabled: boolean): React.CSSProperties =>
  disabled ? { opacity: 0.5, cursor: 'default' } : {}

// Prototype hover states (inline styles can't express :hover, so the page
// renders this once; `!important` beats the inline base colours). Selected /
// disabled controls are excluded, as in the prototype.
export const BUILDER_HOVER_CSS = `
.bsb-back:hover{background:${T.gray100}!important;color:${T.gray800}!important}
.bsb-ghost:hover:not(:disabled){background:${T.gray100}!important;color:${T.gray800}!important}
.bsb-blue:hover:not(:disabled){background:${T.blueH}!important}
.bsb-card:not([aria-pressed="true"]):not([aria-checked="true"]):not(:disabled):hover{border-color:${T.gray300}!important;background:${T.gray50}!important}
.bsb-seg:not([aria-checked="true"]):hover{background:${T.gray50}!important}
.bsb-dev:not([aria-pressed="true"]):hover{border-color:${T.gray300}!important}
`
